import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

/* when using this component ensure _overlay scss partial is on the page */

const PopUp = ({ align, children, dataTestid, onClose, popUpWidth, popUpHeight, allowToClose = true }) => {
    const popupStyle = {
        maxWidth: popUpWidth,
        minHeight: popUpHeight
    };

    const popupWrapperId = 'PopUpWrapper';
    const popupRef = useRef();
    const [root, setRoot] = useState(null);
    const scrollY = useRef();
    const [show, setShow] = useState(false);

    const handleClose = e => {
        e.preventDefault();
        onClose();
    };

    const handleEscapeKey = e => {
        if (e.keyCode === 27 && popupRef.current.nextElementSibling === null) {
            onClose();
        }
    };

    useEffect(() => {
        const existingRoot = document.querySelector(`#${popupWrapperId}`);
        const html = document.querySelector('html');
        const pageOuterWrapper = document.querySelector('.re-page-outer-wrapper');

        scrollY.current = window.scrollY;

        if (existingRoot) {
            setRoot(existingRoot);
        } else {
            const div = document.createElement('div');
            div.setAttribute('id', popupWrapperId);
            setRoot(document.body.appendChild(div));
        }

        if (html) {
            html.classList.add('re-overlay-open');
        }

        if (pageOuterWrapper) {
            pageOuterWrapper.classList.add('re-overlay-stick-content');
            pageOuterWrapper.style.top = `-${scrollY.current}px`;
        }

        document.addEventListener('keydown', handleEscapeKey);
        window.scrollTo(0, 0);

        setShow(true);

        return () => {
            if (popupRef.current.previousElementSibling === null) {
                const html2 = document.querySelector('html');
                const pageOuterWrapper2 = document.querySelector('.re-page-outer-wrapper');

                if (html2) {
                    html2.classList.remove('re-overlay-open');
                }

                if (pageOuterWrapper2) {
                    pageOuterWrapper2.classList.remove('re-overlay-stick-content');
                    pageOuterWrapper2.style.top = 'auto';
                }
            }

            document.removeEventListener('keydown', handleEscapeKey);
            window.scrollTo(0, scrollY.current);
        };
    }, []);

    return (
        <React.Fragment>
            {show &&
                root &&
                createPortal(
                    <aside
                        ref={popupRef}
                        aria-modal="true"
                        tabIndex="-1"
                        role="dialog"
                        className={`re-popup-component re-popup-align-${align}`}
                        data-testid={dataTestid}
                    >
                        <div
                            className="overlay-background"
                            onClick={e => (allowToClose ? handleClose(e) : null)}
                            data-testid={`${dataTestid}-background`}
                        />
                        <div className="re-overlay-wrapper-intro" style={popupStyle}>
                            {allowToClose ? (
                                <button
                                    aria-label="Close Popup"
                                    aria-labelledby="close-popup"
                                    onClick={handleClose}
                                    className="re-overlay-wrapper-close-btn re-icon re-icon-close"
                                    data-testid={`${dataTestid}-close`}
                                >
                                    <span id="close-popup" className="visually-hidden">
                                        Close
                                    </span>
                                </button>
                            ) : null}
                            {children}
                        </div>
                    </aside>,
                    root
                )}
        </React.Fragment>
    );
};

PopUp.propTypes = {
    align: PropTypes.string,
    children: PropTypes.node.isRequired,
    dataTestid: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    popUpWidth: PropTypes.string,
    popUpHeight: PropTypes.string
};

PopUp.defaultProps = {
    align: 'center',
    dataTestid: 'PopUpComponent',
    popUpHeight: '425px',
    popUpWidth: '450px'
};

export default PopUp;
